import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Papa from 'papaparse';

import Dropzone from '../../../../shared/dropzone/Dropzone';

import { procedureFlows } from '../../../../../constants/cases';

const commonFieldDefinitions = [
  { field: 'Type', measurement: 'var_or_val' },
  { field: 'JLCA', measurement: 'JLCA_deg' },
  { field: 'HKA', measurement: 'HKA_obtuse_deg' },
  { field: 'Pre-Op_HKA', measurement: 'HKA_acute_deg' },
  { field: 'WBL', measurement: 'WBL_mm' },
  { field: 'TW', measurement: 'TW_mm' },
  { field: '%TW', measurement: 'TW_%' },
  { field: 'JL_A', measurement: 'JL_A_mm' },
  { field: 'TW_JL_A', measurement: 'TW_JL_A_deg' },
  { field: 'JL_CoFH', measurement: 'JL_CoFH_mm' },
  { field: 'TW_JL_CoFH', measurement: 'TW_JL_CoFH_deg' },
  { field: 'mLDFA', measurement: 'mLDFA_deg' },
  { field: 'MPTA', measurement: 'MPTA_deg' },
  { field: 'PDFA', measurement: 'PDFA_deg' },
  { field: 'PPTA', measurement: 'PPTA_deg' },
];

const normalizeMeasurement = (measurement) =>
  String(measurement || '')
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, '');

const isDfoFlow = (procedureFlow) =>
  procedureFlow === procedureFlows.DFO_Lateral || procedureFlow === procedureFlows.DFO_Medial;

const getFieldDefinitions = (procedureFlow) => [
  ...commonFieldDefinitions,
  isDfoFlow(procedureFlow)
    ? { field: 'FW_Osteotomy', measurement: 'FW_Osteotomy_mm' }
    : { field: 'TW_Osteotomy', measurement: 'TW_Osteotomy_mm' },
];

const getHeader = (headers, expectedHeader) =>
  headers.find((header) => normalizeMeasurement(header) === normalizeMeasurement(expectedHeader));

const getParseErrorMessage = (error) => {
  const row = Number.isInteger(error?.row) ? ` on row ${error.row + 2}` : '';
  return `${error?.message || 'The CSV could not be parsed'}${row}`;
};

export const parseAlignmentCsv = (csvText, procedureFlow) => {
  const result = Papa.parse(csvText, {
    header: true,
    skipEmptyLines: 'greedy',
    transformHeader: (header) =>
      String(header || '')
        .replace(/^\uFEFF/, '')
        .trim(),
  });

  if (result.errors.length) {
    throw new Error(getParseErrorMessage(result.errors[0]));
  }

  const headers = result.meta.fields || [];
  const measurementHeader = getHeader(headers, 'Measurement Name');
  const valueHeader = getHeader(headers, 'Value');

  if (!measurementHeader || !valueHeader) {
    throw new Error('The CSV must contain Measurement Name and Value columns.');
  }

  if (!result.data.length) {
    throw new Error('The CSV does not contain any measurement data.');
  }

  const measurements = new Map();

  result.data.forEach((row) => {
    const measurement = normalizeMeasurement(row[measurementHeader]);
    const value = row[valueHeader];

    if (measurement && value !== null && value !== undefined && value !== '') {
      measurements.set(measurement, value);
    }
  });

  const fieldDefinitions = getFieldDefinitions(procedureFlow);
  const importedValues = {};
  const importedFields = [];
  const invalidFields = [];
  let importedType;

  fieldDefinitions.forEach(({ field, measurement }) => {
    const rawValue = measurements.get(normalizeMeasurement(measurement));

    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return;
    }

    if (field === 'Type') {
      const normalizedType = String(rawValue).trim().toLowerCase();

      if (!['varus', 'valgus'].includes(normalizedType)) {
        invalidFields.push(`${measurement} must be Varus or Valgus`);
        return;
      }

      importedType = normalizedType === 'varus' ? 'Varus' : 'Valgus';

      if (procedureFlow === procedureFlows.DFO_Medial && importedType === 'Varus') {
        invalidFields.push('Type must be Valgus for DFO Medial');
        return;
      }

      importedFields.push(field);
      return;
    }

    const numericValue = Number(rawValue);

    if (!Number.isFinite(numericValue)) {
      invalidFields.push(`${measurement} must be a number`);
      return;
    }

    importedValues[field] = numericValue;
    importedFields.push(field);
  });

  if (importedValues.HKA !== undefined && importedValues['Pre-Op_HKA'] === undefined) {
    importedValues['Pre-Op_HKA'] = (18000 - importedValues.HKA * 100) / 100;
    importedFields.push('Pre-Op_HKA');
  }

  if (invalidFields.length) {
    throw new Error(invalidFields.join('. '));
  }

  if (!importedFields.length) {
    throw new Error('No supported Step 3 measurements were found in this CSV.');
  }

  const missingFields = fieldDefinitions.map(({ field }) => field).filter((field) => !importedFields.includes(field));

  return {
    importedValues,
    importedType,
    importedFields,
    missingFields,
    fieldDefinitions,
  };
};

const AlignmentCsvImport = ({ activeCase, onImport }) => {
  const [files, setFiles] = useState([]);
  const [message, setMessage] = useState(null);
  const fieldDefinitions = getFieldDefinitions(activeCase.procedureFlow);
  const osteotomyField = fieldDefinitions[fieldDefinitions.length - 1];

  const handleUpload = async (uploadedFiles) => {
    const file = uploadedFiles[0];

    setFiles(file ? [file] : []);
    setMessage(null);

    if (!file) {
      return;
    }

    try {
      const csvText = await file.text();
      const { importedValues, importedType, importedFields, missingFields } = parseAlignmentCsv(
        csvText,
        activeCase.procedureFlow
      );

      onImport(importedValues, importedType);

      const notices = ['Manual validation is still required'];

      if (missingFields.length) {
        notices.push(`Missing fields were left unchanged: ${missingFields.join(', ')}`);
      }

      setMessage({
        type: missingFields.length ? 'warning' : 'success',
        text: `Imported ${importedFields.length} value${importedFields.length === 1 ? '' : 's'} from ${
          file.name
        }. ${notices.join('. ')}.`,
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: `Could not import ${file.name}: ${error.message}`,
      });
    }
  };

  return (
    <div className="modal-item-block">
      <div className="block-header">Import Alignment Data (.csv)</div>
      <div className="secondary font-size-sm m-b-md">
        Upload the pre-op measurements CSV with <span className="font-bold">Measurement Name</span> and{' '}
        <span className="font-bold">Value</span> columns.
      </div>
      <Dropzone
        files={files}
        onUpload={handleUpload}
        type="csv"
        text="Drag and drop a .csv file here or click to import"
        multiple={false}
        hideFilesList
      />
      {/*<div className="m-t-sm font-size-sm">*/}
      {/*  <span className="font-bold">Imported measurements:</span> var_or_val, HKA_acute_deg, HKA_obtuse_deg, JLCA_deg,*/}
      {/*  WBL_mm, TW_mm, TW_%, JL_A_mm, TW_JL_A_deg, {osteotomyField.measurement}, JL_CoFH_mm, TW_JL_CoFH_deg, mLDFA_deg,*/}
      {/*  MPTA_deg, PDFA_deg, PPTA_deg*/}
      {/*</div>*/}
      {message && <div className={`alert ${message.type}`}>{message.text}</div>}
    </div>
  );
};

AlignmentCsvImport.propTypes = {
  activeCase: PropTypes.object,
  onImport: PropTypes.func,
};

export default AlignmentCsvImport;
